import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";
import ExternalLaunchers from "@/components/ExternalLaunchers";
import {
  LayoutDashboard, Receipt, Plus, AlertTriangle, FileSignature,
  Settings, LogOut, Shield, Building2, Plug, Wallet,
} from "lucide-react";
import { Button } from "@/components/ui/button";

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const merchantNav = [
    { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true },
    { to: "/dashboard/transactions", label: "Transactions", icon: Receipt },
    { to: "/dashboard/transactions/new", label: "New Charge", icon: Plus },
    { to: "/dashboard/gateway", label: "Transaction Gateway", icon: Plug },
    { to: "/dashboard/chargebacks", label: "Chargebacks", icon: AlertTriangle },
    { to: "/dashboard/credit", label: "Credit App", icon: FileSignature },
    { to: "/dashboard/billing", label: "Billing & Plan", icon: Wallet },
    { to: "/dashboard/apply", label: "Merchant Profile", icon: Building2 },
  ];

  const adminNav = [
    { to: "/dashboard/admin", label: "Admin Console", icon: Shield },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-900 flex" data-testid="dashboard-layout">
      <aside className="w-64 border-r border-slate-200 bg-[#FFFFFF] flex flex-col" data-testid="dashboard-sidebar">
        <div className="p-6 border-b border-slate-200">
          <Logo size="md" />
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {user?.role === "admin" ? (
            <>
              <SectionLabel>Admin</SectionLabel>
              {adminNav.map((it) => <NavItem key={it.to} {...it} />)}
              <SectionLabel className="mt-6">View as Merchant</SectionLabel>
              {merchantNav.map((it) => <NavItem key={it.to} {...it} />)}
            </>
          ) : (
            <>
              <SectionLabel>Merchant</SectionLabel>
              {merchantNav.map((it) => (
                <NavItem key={it.to} {...it} />
              ))}
            </>
          )}
        </nav>
        <div className="p-4 border-t border-slate-200">
          <div className="px-3 py-2 mb-3">
            <div className="text-xs uppercase tracking-wider text-slate-400">Signed in as</div>
            <div className="mt-1 text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.name}</div>
            <div className="text-xs text-slate-500 truncate">{user?.email}</div>
            {user?.role === "admin" && (
              <span className="mt-2 inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#3B82F6]/15 text-[#3B82F6] border border-[#3B82F6]/30">Admin</span>
            )}
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-slate-600 hover:text-slate-900 hover:bg-slate-50"
            onClick={async () => { await logout(); navigate("/"); }}
            data-testid="sidebar-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-6">
          <ExternalLaunchers />
        </div>
        <div className="max-w-7xl mx-auto p-6 lg:p-10 pt-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function NavItem({ to, label, icon: Icon, end }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
          isActive
            ? "bg-slate-100 text-slate-900"
            : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"
        }`
      }
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <Icon className="w-4 h-4" /> {label}
    </NavLink>
  );
}

function SectionLabel({ children, className = "" }) {
  return <div className={`px-3 mb-2 text-[10px] uppercase tracking-[0.18em] text-slate-400 ${className}`}>{children}</div>;
}
